import React from 'react';
import { Paper, Typography, Box, Button, Grid, TextField, InputAdornment, Switch, FormControlLabel, useTheme } from '@mui/material';

const BorderPanel = ({ options, onChange, onApplyPreset, onApply }) => {
  const theme = useTheme();
  const [local, setLocal] = React.useState(options || { color: theme.palette.common.white, strokeWidth: 6, inset: 0, dashed: false, radius: 0, locked: true });

  React.useEffect(() => {
    setLocal(options || { color: theme.palette.common.white, strokeWidth: 6, inset: 0, dashed: false, radius: 0, locked: true });
  }, [options]);

  const presets = [
    { id: 'thin-white', label: 'Thin White', color: theme.palette.common.white, strokeWidth: 2, inset: 0, dashed: false, radius: 0 },
    { id: 'thick-black', label: 'Thick Black', color: theme.palette.common.black, strokeWidth: 12, inset: 0, dashed: false, radius: 0 },
    { id: 'dashed-gray', label: 'Dashed Gray', color: theme.palette.grey[300], strokeWidth: 4, inset: 10, dashed: true, radius: 0 },
    { id: 'rounded', label: 'Rounded', color: theme.palette.common.white, strokeWidth: 6, inset: 8, dashed: false, radius: 16 },
  ];

  const update = (patch) => {
    const next = { ...local, ...patch };
    setLocal(next);
    onChange?.(next);
  };

  return (
    <Paper sx={{ width: '100%', bgcolor: theme.palette.background.paper, borderRadius: 0, borderRight: `1px solid ${theme.palette.divider}`, p: 1, mt: 1 }}>
      <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary, mb: 1 }}>Border</Typography>
      <Grid container spacing={1}>
        {presets.map(p => (
          <Grid item xs={6} key={p.id}>
            <Button size="small" variant="outlined" sx={{ width: '100%', color: theme.palette.text.primary, borderColor: theme.palette.divider, textTransform: 'none' }} onClick={() => onApplyPreset?.(p)}> {p.label} </Button>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
        <TextField
          size="small"
          label="Color"
          type="color"
          value={local.color}
          onChange={(e) => update({ color: e.target.value })}
          sx={{
            '& .MuiOutlinedInput-root': {
              p: 0.5,
              '& input': { height: 32, padding: 0, minWidth: 0 },
              '& fieldset': { borderColor: theme.palette.divider },
            },
          }}
        />
        <TextField size="small" label="Width" type="number" value={local.strokeWidth} onChange={(e) => update({ strokeWidth: Number(e.target.value) || 0 })}
          InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
          InputLabelProps={{ style: { color: theme.palette.text.secondary } }}
          sx={{ '& .MuiOutlinedInput-root': { color: theme.palette.text.primary, '& fieldset': { borderColor: theme.palette.divider } } }}
        />
        <TextField size="small" label="Inset" type="number" value={local.inset} onChange={(e) => update({ inset: Number(e.target.value) || 0 })}
          InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
          InputLabelProps={{ style: { color: theme.palette.text.secondary } }}
          sx={{ '& .MuiOutlinedInput-root': { color: theme.palette.text.primary, '& fieldset': { borderColor: theme.palette.divider } } }}
        />
        <TextField size="small" label="Radius" type="number" value={local.radius} onChange={(e) => update({ radius: Number(e.target.value) || 0 })}
          InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
          InputLabelProps={{ style: { color: theme.palette.text.secondary } }}
          sx={{ '& .MuiOutlinedInput-root': { color: theme.palette.text.primary, '& fieldset': { borderColor: theme.palette.divider } } }}
        />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
        <FormControlLabel control={<Switch checked={!!local.dashed} onChange={(e) => update({ dashed: e.target.checked })} />} label={<span style={{ color: theme.palette.text.primary }}>Dashed</span>} />
        <FormControlLabel control={<Switch checked={!!local.locked} onChange={(e) => update({ locked: e.target.checked })} />} label={<span style={{ color: theme.palette.text.primary }}>Lock</span>} />
      </Box>
      <Button fullWidth variant="contained" size="small" onClick={() => onApply?.(local)} sx={{ mt: 1, textTransform: 'none', bgcolor: theme.palette.background.default, '&:hover': { bgcolor: theme.palette.action.hover }, color: theme.palette.text.primary }}>Add / Update Border</Button>
    </Paper>
  );
};

export default BorderPanel;
